import { authedApiFetch } from '../../../utils/Api';

/**
 * Service for handling email sync configuration API calls
 */
const emailSyncConfigService = {
	/**
	 * Update tenant attributes
	 * @param {string} tenantId - The tenant ID
	 * @param {object} keyValues - Tenant key value pair, ex: { email_body_sync: true }
	 * @returns {Promise<Object>} - Updated tenant data
	 */
	updateTenant: async (tenantId, keyValue) => {
		const response = await authedApiFetch({
			endpoint: `/tenants/${tenantId}`,
			method: 'PATCH',
			payload: {
				tenant: keyValue,
			},
		});
		return response;
	},

	/**
	 * Get email rules
	 * @param {boolean} tenantLevel - Whether to get tenant level rules
	 * @param {string} tenantId - The tenant ID
	 * @param {boolean} inclusionEnabled - Whether to get inclusion or exclusion rules
	 * @returns {Promise<Object>} - Email rules
	 */
	getEmailRules: async (tenantLevel, tenantId, inclusionEnabled) => {
		const response = await authedApiFetch({
			endpoint: tenantLevel
				? `/tenants/${tenantId}/email_rules`
				: '/email_rules',
			method: 'GET',
			params: {
				rule_type: inclusionEnabled ? 'inclusion' : 'exclusion',
				tenant_level: tenantLevel,
			},
		});
		return response;
	},

	/**
	 * Create new email rule
	 * @param {boolean} tenantLevel - Whether to create tenant or user level rule
	 * @param {string} tenantId - The tenant ID
	 * @param {boolean} inclusionEnabled - Whether to create inclusion or exclusion rule
	 * @param {string} criteriaType - The criteria type: domain, sender_type, specific_email
	 * @param {string} value - The value
	 * @returns {Promise<Object>} - The created email rule
	 */
	createEmailRule: async (
		tenantLevel,
		tenantId,
		inclusionEnabled,
		criteriaType,
		value
	) => {
		const endpoint = tenantLevel
			? `/tenants/${tenantId}/email_rules`
			: '/email_rules';

		const response = await authedApiFetch({
			endpoint,
			method: 'POST',
			payload: {
				email_rule: {
					rule_type: inclusionEnabled ? 'inclusion' : 'exclusion',
					criteria_type: criteriaType,
					value: value,
				},
			},
		});
		return response;
	},

	/**
	 * Delete email rule
	 * @param {boolean} tenantLevel - Whether to delete tenant or user level rule
	 * @param {string} tenantId - The tenant ID
	 * @param {string} ruleId - The rule ID
	 * @returns {Promise<Object>} - The deleted email rule
	 */
	deleteEmailRule: async (tenantLevel, tenantId, ruleId) => {
		const endpoint = tenantLevel
			? `/tenants/${tenantId}/email_rules/${ruleId}`
			: `/email_rules/${ruleId}`;

		const response = await authedApiFetch({
			endpoint,
			method: 'DELETE',
		});

		return response;
	},
};

export default emailSyncConfigService;
